import { useClose } from "@headlessui/react";
import { formatFileNames, type SuretyQuoteId } from "@inrev/common";
import { fileViewerControl } from "@portal/api/files";
import { type ReactNode, useMemo, useState } from "react";
import { type AttachmentContext, trackAttachmentAction } from "../../analytics";
import { useGetFileUrl } from "../../api";
import { BondRequestExtractionSummaryPanel } from "../features/bond-request-extraction/BondRequestExtractionSummaryPanel";
import { ConfirmationModalClickWrapper } from "../layout/ConfirmationModalClickWrapper";
import { cn } from "../lib/utils";
import { type PDFViewerControl } from "../patterns/pdf-viewer";
import { Button, Icon } from "../primitives";
import { Menu } from "../ui/Menu";
import { PDFViewerModal } from "../ui/PDFViewerModal";
import { Spinner } from "../ui/Spinner";
import { isViewableBondRequestExtraction, UploadedDocumentRow } from "./types";

export type UploadedDocumentRowActionsConfig<T extends UploadedDocumentRow = UploadedDocumentRow> =
	{
		downloadBaseUrl: string;
		downloadQueryKeyPrefix: string;
		allowDelete: boolean;
		onDeleteAttachment: (attachmentId: string) => void;
		deletingAttachmentId: string | null;
		leadingSlot?: ReactNode;
		buildAttachmentDeleteConfirmation?: (row: T) => ReactNode;
		analyticsContext: AttachmentContext;
		quoteId?: SuretyQuoteId;
	};

type ActionsMenuPanelProps<T extends UploadedDocumentRow> = Omit<
	UploadedDocumentRowActionsConfig<T>,
	"leadingSlot" | "deletingAttachmentId"
> & {
	row: T;
	onViewPdf: () => void;
	onViewSummary: () => void;
	onViewExtraction: () => void;
	canViewBondRequestExtraction: boolean;
};

const ActionsMenuPanel = <T extends UploadedDocumentRow>({
	row,
	downloadBaseUrl,
	downloadQueryKeyPrefix,
	allowDelete,
	onDeleteAttachment,
	buildAttachmentDeleteConfirmation,
	onViewPdf,
	onViewSummary,
	onViewExtraction,
	canViewBondRequestExtraction,
	analyticsContext,
}: ActionsMenuPanelProps<T>) => {
	const close = useClose();
	const { getFileUrl } = useGetFileUrl();
	const attachmentId = row.id;
	const isPdf = row.file.extension === "pdf";
	const hasContractSummary = row.contractExtraction?.pdfFileId != null;
	const fileLabel = formatFileNames([row.file]) ?? `${row.file.name}.${row.file.extension}`;
	const handleDownload = () => {
		trackAttachmentAction("download", analyticsContext);
		getFileUrl({
			baseUrl: `${downloadBaseUrl}/${attachmentId}`,
			queryKey: [downloadQueryKeyPrefix, attachmentId, true],
			asDownload: true,
		});
		close();
	};

	const menuItemClass =
		"flex h-auto w-full items-center justify-start gap-2 rounded-md px-2 py-1.5 text-left text-[13px] font-medium text-secondary hover:bg-surface-dark/55";

	const attachmentDeleteMessage =
		buildAttachmentDeleteConfirmation && (row.financialExtraction || row.wipExtraction) ? (
			buildAttachmentDeleteConfirmation(row)
		) : (
			<div className="w-[375px] text-center break-words">
				Are you sure you want to delete
				<br />
				{fileLabel}?
			</div>
		);

	const attachmentDeleteButton = allowDelete && (
		<ConfirmationModalClickWrapper
			message={attachmentDeleteMessage}
			confirmButtonText="Delete"
			confirmationButtonColor="danger"
			confirmTrackingId={`${analyticsContext}-attachment-delete-confirm`}
			cancelTrackingId={`${analyticsContext}-attachment-delete-cancel`}
			onConfirm={() => {
				trackAttachmentAction("delete", analyticsContext);
				onDeleteAttachment(attachmentId);
				close();
			}}
			onCancel={close}
		>
			<Button
				variant="ghost"
				className={cn(menuItemClass, "text-danger hover:bg-surface-danger")}
				leadingIcon={<Icon type="trash" size="small" color="danger" />}
				trackingId={`${analyticsContext}-attachment-delete`}
			>
				Delete
			</Button>
		</ConfirmationModalClickWrapper>
	);

	return (
		<div className="flex shrink-0 flex-col gap-0.5 rounded-md border border-bc-default bg-surface-base px-1 py-1.5">
			{isPdf && (
				<Button
					variant="ghost"
					className={menuItemClass}
					onClick={() => {
						trackAttachmentAction("preview", analyticsContext);
						onViewPdf();
						close();
					}}
					leadingIcon={<Icon type="eye" size="small" color="secondary" />}
					trackingId={`${analyticsContext}-attachment-preview`}
				>
					View
				</Button>
			)}
			{hasContractSummary && (
				<Button
					variant="ghost"
					className={menuItemClass}
					onClick={() => {
						trackAttachmentAction("view_summary", analyticsContext);
						onViewSummary();
						close();
					}}
					leadingIcon={<Icon type="document-chart-bar" size="small" color="secondary" />}
					data-testid={`uploaded-doc-view-summary-${attachmentId}`}
					trackingId={`${analyticsContext}-attachment-summary-preview`}
				>
					View Summary
				</Button>
			)}
			{canViewBondRequestExtraction && (
				<Button
					variant="ghost"
					className={menuItemClass}
					onClick={() => {
						trackAttachmentAction("view_extraction", analyticsContext);
						onViewExtraction();
						close();
					}}
					leadingIcon={<Icon type="document-text" size="small" color="secondary" />}
					data-testid={`uploaded-doc-view-extraction-${attachmentId}`}
					trackingId={`${analyticsContext}-attachment-extraction-preview`}
				>
					View Extraction
				</Button>
			)}
			<Button
				variant="ghost"
				className={menuItemClass}
				onClick={handleDownload}
				leadingIcon={<Icon type="arrow-down-tray" size="small" color="secondary" />}
				trackingId={`${analyticsContext}-attachment-download`}
			>
				Download
			</Button>
			{attachmentDeleteButton}
		</div>
	);
};

type UploadedDocumentRowActionsCellProps<T extends UploadedDocumentRow> =
	UploadedDocumentRowActionsConfig<T> & {
		row: T;
	};

export const UploadedDocumentRowActionsCell = <T extends UploadedDocumentRow>({
	row,
	downloadBaseUrl,
	downloadQueryKeyPrefix,
	allowDelete,
	onDeleteAttachment,
	deletingAttachmentId,
	buildAttachmentDeleteConfirmation,
	leadingSlot,
	analyticsContext,
	quoteId,
}: UploadedDocumentRowActionsCellProps<T>) => {
	const attachmentId = row.id;
	const isDeleting = deletingAttachmentId === attachmentId;
	const isPdf = row.file.extension === "pdf";
	const summaryPdfFileId = row.contractExtraction?.pdfFileId ?? undefined;
	const extractionView =
		quoteId != null && isViewableBondRequestExtraction(row.bondRequestExtraction)
			? { extractionId: row.bondRequestExtraction.id, quoteId }
			: undefined;
	const [pdfOpen, setPdfOpen] = useState(false);
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [extractionOpen, setExtractionOpen] = useState(false);

	const documentPreview = useMemo<PDFViewerControl | undefined>(
		() =>
			isPdf
				? {
						fetchUrl: `${downloadBaseUrl}/${attachmentId}`,
						queryKey: [downloadQueryKeyPrefix, attachmentId, true],
						fileId: row.file.id,
					}
				: undefined,
		[isPdf, downloadBaseUrl, downloadQueryKeyPrefix, attachmentId, row.file.id],
	);
	const summaryControl = useMemo(
		() => (summaryPdfFileId != null ? fileViewerControl(summaryPdfFileId) : undefined),
		[summaryPdfFileId],
	);

	return (
		<div className="relative flex min-h-8 items-center justify-center gap-1">
			{leadingSlot}
			<Menu
				testId={`uploaded-doc-actions-${attachmentId}`}
				trackingId={`${analyticsContext}-attachment-actions`}
				ariaLabel="Document actions"
				button={
					isDeleting ? (
						<Spinner
							containerClassName="h-4 w-4"
							spinnerClassName="h-4 w-4 border-2 border-solid border-bc-stronger border-t-transparent"
						/>
					) : (
						<Icon type="dots-vertical" size="small" color="tertiary" />
					)
				}
				buttonClassName={cn(
					"outline-none flex h-6 w-6 shrink-0 items-center justify-center rounded p-0 text-tertiary hover:bg-surface-neutral hover:text-secondary",
					isDeleting && "pointer-events-none opacity-50",
				)}
				panelClassName="mt-1 bg-surface-base shadow-dropdown-shadow"
				anchor="bottom end"
			>
				<ActionsMenuPanel
					row={row}
					downloadBaseUrl={downloadBaseUrl}
					downloadQueryKeyPrefix={downloadQueryKeyPrefix}
					allowDelete={allowDelete}
					onDeleteAttachment={onDeleteAttachment}
					buildAttachmentDeleteConfirmation={buildAttachmentDeleteConfirmation}
					analyticsContext={analyticsContext}
					onViewPdf={() => setPdfOpen(true)}
					onViewSummary={() => setSummaryOpen(true)}
					onViewExtraction={() => setExtractionOpen(true)}
					canViewBondRequestExtraction={extractionView != null}
				/>
			</Menu>
			{pdfOpen && documentPreview != null && (
				<PDFViewerModal
					onClose={() => setPdfOpen(false)}
					control={documentPreview}
					header={row.file.name}
					trackingId={`${analyticsContext}-attachment-viewer`}
				/>
			)}
			{summaryOpen && summaryControl != null && (
				<PDFViewerModal
					onClose={() => setSummaryOpen(false)}
					control={summaryControl}
					header="Contract Summary"
					trackingId={`${analyticsContext}-contract-summary-viewer`}
				/>
			)}
			{extractionOpen && extractionView != null && (
				<BondRequestExtractionSummaryPanel
					extractionId={extractionView.extractionId}
					quoteId={extractionView.quoteId}
					fileName={formatFileNames([row.file])}
					documentPreview={documentPreview}
					onClose={() => setExtractionOpen(false)}
				/>
			)}
		</div>
	);
};
